import { useParams } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  Divider,
  Chip,
  Alert,
} from '@mui/material';
import PageLoader from '../../Components/Spinner/PageLoader';
import { getStatusLabel } from '../../Utils/Label/status-label';
import { useSingleIDCardQuery } from './api/idCardEndPoint';
import { useEffect, useState } from 'react';
import ModalsWrapper from '../../Components/Modals/ModalsWrapper';
import PdfAndImageView from '../../Components/pdfAndImageView/PdfAndImageView';
import { CommonTypoGraphy } from '../../Components/pdfAndImageView/CommonTypoGraphy';
import OfflinePaymentCardInformation from '../new-member-applications/IndexFile/paymentInformation/offline/OfflinePaymentCardInformation';
import CommonDetailItem, {
  formatDate,
  formatValue,
} from '../../Components/CommonDetailItem/CommonDetailItem';

const SingleIdCard = () => {
  const { cardID } = useParams();
  const { singleIdCard, isLoading, refetch } = useSingleIDCardQuery(Number(cardID));
  const [open, setOpen] = useState<boolean>(false);
  const [reload, setReload] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<string | null>('');

  useEffect(() => {
    refetch?.();
  }, [reload, refetch]);

  return (
    <>
      {!isLoading ? (
        <Box sx={{ width: '100%', mt: { xs: 4, sm: 5 }, mb: 3 }}>
          <Card
            sx={{
              width: '100%',
              borderRadius: 2,
              boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
              bgcolor: '#ffffff',
            }}
          >
            <CardHeader
              title={
                <Typography
                  variant='h6'
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.125rem' },
                    fontWeight: 700,
                    color: '#1e293b',
                  }}
                >
                  ID Card Details
                </Typography>
              }

              sx={{
                borderBottom: '1px solid #edf2f7',
                py: 1.25,
                px: { xs: 2, sm: 2.5 },
                backgroundColor: '#fafbfc',
              }}
            />
            <CardContent
              sx={{
                p: { xs: 2, sm: 2.5 },
                '&:last-child': { pb: { xs: 2, sm: 2.5 } },
              }}
            >
              {singleIdCard ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Rejection / Note banner if any */}
                  {singleIdCard?.rejected_reason && (
                    <Alert
                      severity='error'
                      sx={{
                        borderRadius: 1.5,
                        py: 0.5,
                        px: 1.5,
                        fontSize: '0.875rem',
                        '& .MuiAlert-message': { py: 0.25 },
                      }}
                    >
                      <Typography
                        variant='subtitle2'
                        fontWeight='bold'
                        component='span'
                        sx={{ fontSize: '0.875rem' }}
                      >
                        Note / Rejection Reason:
                      </Typography>{' '}
                      {singleIdCard.rejected_reason}
                    </Alert>
                  )}

                  {/* Section 1: Applicant Information */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box
                        sx={{
                          width: 3.5,
                          height: 15,
                          bgcolor: 'primary.main',
                          borderRadius: 1,
                        }}
                      />
                      <Typography
                        variant='subtitle2'
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.925rem',
                          color: '#0f172a',
                          letterSpacing: 0.1,
                        }}
                      >
                        Applicant Information
                      </Typography>
                    </Box>
                    <Grid container rowSpacing={0.75} columnSpacing={2}>
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Applicant Name'
                        value={formatValue(singleIdCard?.applicant_name)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Designation'
                        value={formatValue(singleIdCard?.designation)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Status'
                        value={
                          singleIdCard?.status
                            ? getStatusLabel(singleIdCard.status)
                            : 'N/A'
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Date of Birth'
                        value={formatDate(singleIdCard?.date_of_birth)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Telephone'
                        value={formatValue(singleIdCard?.telephone)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Cellphone'
                        value={formatValue(singleIdCard?.cellphone)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Email'
                        value={formatValue(singleIdCard?.email)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Blood Group'
                        value={formatValue(singleIdCard?.blood_group)}
                      />
                    </Grid>
                  </Box>

                  <Divider sx={{ borderColor: '#f1f5f9' }} />

                  {/* Section 2: Uploaded Documents */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box
                        sx={{
                          width: 3.5,
                          height: 15,
                          bgcolor: 'primary.main',
                          borderRadius: 1,
                        }}
                      />
                      <Typography
                        variant='subtitle2'
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.925rem',
                          color: '#0f172a',
                          letterSpacing: 0.1,
                        }}
                      >
                        Uploaded Documents
                      </Typography>
                    </Box>
                    <Grid container rowSpacing={0.75} columnSpacing={2}>
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='NID File'
                        value={
                          singleIdCard?.member_nid_file ? (
                            <CommonTypoGraphy
                              name='View NID File'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(singleIdCard.member_nid_file);
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Signature File'
                        value={
                          singleIdCard?.signature_file ? (
                            <CommonTypoGraphy
                              name='View Signature'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(singleIdCard.signature_file);
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Photo'
                        value={
                          singleIdCard?.photo ? (
                            <CommonTypoGraphy
                              name='View Photo'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(singleIdCard.photo);
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                    </Grid>
                  </Box>

                  {/* Section 3: Payment Info */}
                  {singleIdCard?.paymentInfo && (
                    <>
                      <Divider sx={{ borderColor: '#f1f5f9' }} />
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Box
                            sx={{
                              width: 3.5,
                              height: 15,
                              bgcolor: 'primary.main',
                              borderRadius: 1,
                            }}
                          />
                          <Typography
                            variant='subtitle2'
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.925rem',
                              color: '#0f172a',
                              letterSpacing: 0.1,
                            }}
                          >
                            Payment Information
                          </Typography>
                          <Chip
                            label={
                              singleIdCard?.paymentInfo?.type === 'online'
                                ? 'Online Payment'
                                : 'Offline Payment'
                            }
                            size='small'
                            color={singleIdCard?.paymentInfo?.type === 'online' ? 'primary' : 'default'}
                            variant='outlined'
                            sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600, ml: 0.5 }}
                          />
                        </Box>
                        <OfflinePaymentCardInformation
                          data={singleIdCard?.paymentInfo}
                        />
                      </Box>
                    </>
                  )}
                </Box>
              ) : (
                <Typography
                  variant='body1'
                  fontWeight='bold'
                  color='text.secondary'
                  textAlign='center'
                  py={4}
                >
                  No Data found
                </Typography>
              )}
            </CardContent>
          </Card>

          <ModalsWrapper
            modalData={{ title: 'View Document' }}
            setShowModal={setOpen}
            showModal={open}
          >
            <PdfAndImageView file={imageFile} />
          </ModalsWrapper>
        </Box>
      ) : (
        <PageLoader />
      )}
    </>
  );
};

export default SingleIdCard;

